import axios from 'axios';
import { clearAuthTokens, getAccessToken, getRefreshToken } from '@/shared/session/auth';
import { externalConnections, LOGOUT_REDIRECT_URI_KEY } from '@/constants/Constants';
import { buildCurrentPageUrl } from '@/shared/utils/url.util';
import { logger } from '@/shared/libs/logger';

type LogoutReason = 'user_initiated' | 'auth_error';

let logoutInProgress = false;

/**
 * True once a logout has started. The page is about to be replaced by the login
 * redirect, so UI should hold its current state rather than render errors.
 */
export function isLogoutInProgress(): boolean {
  return logoutInProgress;
}

export async function logout({ reason }: { reason: LogoutReason }) {
  logoutInProgress = true;

  // Resolved before anything is awaited: the app can still navigate while the
  // revoke call is in flight, and the user should come back to where they were.
  const redirectTarget =
    reason === 'auth_error' ? buildLogoutUrl(buildCurrentPageUrl()) : externalConnections.logout;

  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();
  clearAuthTokens();

  try {
    await axios.post(
      externalConnections.loginApiLogout,
      { refresh_token: refreshToken },
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );
  } catch (error) {
    logger.error('Error revoking token', undefined, error);
  }

  globalThis.location.replace(redirectTarget);
}

export function buildLogoutUrl(redirectUri: string | null): string {
  const logoutUrlString = externalConnections.logout;

  if (redirectUri !== null && URL.canParse(redirectUri) && URL.canParse(logoutUrlString)) {
    const logoutUrl = new URL(logoutUrlString);
    logoutUrl.searchParams.set(LOGOUT_REDIRECT_URI_KEY, redirectUri);
    return logoutUrl.toString();
  }

  return logoutUrlString;
}

export default logout;
